/**
 * ServiceActivityBreakdownPanel
 * ─────────────────────────────────────────────────────────────────────────────
 * Phase 1.5 — Admin-only "Service Activity Breakdown" panel inside the Shift
 * Drawer / Shift Detail page.
 *
 * Hard invariants honoured (no exceptions):
 *  - Staff still see ONE shift, ONE clock-in, ONE clock-out.
 *  - Invoice / claim / funding / payroll behaviour is **not** affected.
 *  - This panel reads/writes ONLY to /api/shifts/{id}/service-allocations.
 *  - When the feature flag `multi_service_shift_allocations_enabled` is OFF
 *    (default in production), the panel is hidden for non-admin viewers and
 *    shows a read-only banner for admins explaining preview mode.
 *  - Operational breakdown only — billing is still on the existing primary
 *    service on the parent shift.
 */
import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { Layers, Plus, Trash2, Pencil, Lock, Info, CheckCircle2, X, Activity, Car, Star, DollarSign, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';

const minutes = (hhmm) => {
  if (!hhmm || !hhmm.includes(':')) return -1;
  const [h, m] = hhmm.split(':');
  const v = parseInt(h, 10) * 60 + parseInt(m, 10);
  return Number.isFinite(v) ? v : -1;
};

const fmtHours = (h) => (h == null ? '—' : `${Number(h).toFixed(2)}h`);

// Build a human-readable label for a funding plan dropdown row.
// Falls back to a short ID suffix only if every meaningful field is missing.
const fundingPlanLabel = (p) => {
  if (!p) return '—';
  const name = p.plan_name || p.name || p.plan_number;
  if (name) {
    // Add date range if available for plans with the same name
    if (p.start_date && p.end_date) {
      return `${name} (${String(p.start_date).slice(0, 10)} → ${String(p.end_date).slice(0, 10)})`;
    }
    return name;
  }
  // Last-resort fallback — show "Plan #<last 6 chars>" rather than the full ObjectId
  if (p.id) return `Plan #${String(p.id).slice(-6)}`;
  return '—';
};

export function ServiceActivityBreakdownPanel({ shift, onChange }) {
  const { hasPermission } = useAuth();
  const canView = hasPermission('service_allocations.view');
  const canCreate = hasPermission('service_allocations.create');
  const canUpdate = hasPermission('service_allocations.update');
  const canDelete = hasPermission('service_allocations.delete');
  // Enterprise Phase 1.5 — admin-only operational summary tile
  const canViewOperationalTile = hasPermission('allocation.view');
  // Enterprise Phase 2A — admin-only preview tile (financial projection, read-only)
  const canViewPreviewTile = hasPermission('allocation.preview');

  const [loading, setLoading] = useState(false);
  const [flagOn, setFlagOn] = useState(false);
  const [rows, setRows] = useState([]);
  const [services, setServices] = useState([]);
  const [fundingPlans, setFundingPlans] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | <allocation_id>
  const [form, setForm] = useState({
    service_catalog_id: '',
    funding_plan_id: '',
    allocation_start_time: '',
    allocation_end_time: '',
    is_primary: false,
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  // Enterprise Phase 1.5 — operational summary state (read-only)
  const [opsSummary, setOpsSummary] = useState(null); // null | { feature_enabled, summary }
  // Enterprise Phase 2A — financial preview state (read-only projection)
  const [preview, setPreview] = useState(null); // null | PreviewDoc
  // Enterprise Phase 2A.5 — preview lines drawer expand/collapse (admin-only)
  const [previewLinesOpen, setPreviewLinesOpen] = useState(false);

  // ── effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!shift?.id || !canView) return;
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const [a, ff] = await Promise.all([
          api.get(`/shifts/${shift.id}/service-allocations`),
          api.get('/shifts/service-allocations/feature-flag'),
        ]);
        if (!active) return;
        setRows((a.data?.data || []).filter(r => r.status !== 'DELETED'));
        setFlagOn(!!ff.data?.data?.enabled);
      } catch (e) {
        if (active) setRows([]);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [shift?.id, canView]);

  // Enterprise Phase 1.5 — fetch operational summary (admin-only, additive).
  // The endpoint short-circuits to a safe stub when the umbrella flag is OFF,
  // so this call is always safe to make for users with `allocation.view`.
  useEffect(() => {
    if (!shift?.id || !canViewOperationalTile) {
      setOpsSummary(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/admin/allocations/operational-summary/${shift.id}`);
        if (active) setOpsSummary(r.data?.data || null);
      } catch {
        if (active) setOpsSummary(null);
      }
    })();
    return () => { active = false; };
  }, [shift?.id, canViewOperationalTile]);

  // Enterprise Phase 2A — fetch financial preview (admin-only, additive).
  // Endpoint is fully READ-ONLY and gated by the preview feature flag;
  // when the flag is OFF the endpoint returns a safe stub and the tile
  // suppresses itself via the render guard.
  useEffect(() => {
    if (!shift?.id || !canViewPreviewTile) {
      setPreview(null);
      return;
    }
    let active = true;
    (async () => {
      try {
        const r = await api.get(`/admin/allocations/preview/${shift.id}`);
        if (active) setPreview(r.data?.data || null);
      } catch {
        if (active) setPreview(null);
      }
    })();
    return () => { active = false; };
  }, [shift?.id, canViewPreviewTile]);

  // Load services + funding plans whenever the editor opens OR the shift's participant changes.
  // Loading on shift change (not only on edit) means the lookup map is available
  // for the read-only allocation row display.
  useEffect(() => {
    if (!shift?.participant_id) return;
    (async () => {
      try {
        const [sc, fp, psi] = await Promise.all([
          api.get('/services/catalog'),
          api.get(`/funding/plans/by-participant/${shift.participant_id}`),
          api.get(`/services/participant-items?participant_id=${shift.participant_id}`).catch(() => ({ data: { data: [] } })),
        ]);
        setServices((sc.data?.data || []).filter(s => s.is_active !== false && !s.is_deleted));
        setFundingPlans(fp.data?.data || fp.data?.items || []);
        setParticipantServiceItems(psi.data?.data || []);
      } catch (e) { /* swallow — form just renders empty selects */ }
    })();
  }, [editing, shift?.participant_id]);

  // Per-participant funded services (NDIS billing safety)
  const [participantServiceItems, setParticipantServiceItems] = useState([]);
  const [showAllServices, setShowAllServices] = useState(false);

  // Lookup map so allocation rows can display the funding plan name instead of the raw ObjectId.
  const fundingPlanLookup = useMemo(() => {
    const map = new Map();
    (fundingPlans || []).forEach(p => { if (p?.id) map.set(p.id, p); });
    return map;
  }, [fundingPlans]);

  // Lookup map so allocation rows can display the service name (and code) instead of the raw ObjectId.
  const serviceLookup = useMemo(() => {
    const map = new Map();
    (services || []).forEach(s => { if (s?.id) map.set(s.id, s); });
    return map;
  }, [services]);

  const serviceLabel = (id) => {
    if (!id) return '—';
    const s = serviceLookup.get(id);
    if (!s) return `Service #${String(id).slice(-6)}`;
    return s.code ? `${s.name} (${s.code})` : s.name;
  };

  const shiftDuration = useMemo(() => {
    const s = minutes(shift?.start_time);
    const e = minutes(shift?.end_time);
    if (s < 0 || e <= s) return 0;
    return (e - s) / 60.0;
  }, [shift?.start_time, shift?.end_time]);

  const allocated = rows.reduce((acc, r) => acc + (Number(r.duration_hours) || 0), 0);
  const remaining = Math.max(0, shiftDuration - allocated);
  const overAllocated = allocated > shiftDuration + 0.05;

  // ── handlers ───────────────────────────────────────────────────────────────
  const reload = async () => {
    if (!shift?.id) return;
    try {
      const a = await api.get(`/shifts/${shift.id}/service-allocations`);
      setRows((a.data?.data || []).filter(r => r.status !== 'DELETED'));
      onChange && onChange();
    } catch { /* ignore */ }
  };

  const openNew = () => {
    setForm({
      service_catalog_id: shift?.service_catalog_id || '',
      funding_plan_id: shift?.funding_plan_id || '',
      allocation_start_time: shift?.start_time || '',
      allocation_end_time: shift?.end_time || '',
      is_primary: rows.length === 0,
      notes: '',
    });
    setEditing('new');
  };

  const openEdit = (row) => {
    setForm({
      service_catalog_id: row.service_catalog_id || '',
      funding_plan_id: row.funding_plan_id || '',
      allocation_start_time: row.allocation_start_time || '',
      allocation_end_time: row.allocation_end_time || '',
      is_primary: !!row.is_primary,
      notes: row.notes || '',
      _version: row.version,
    });
    setEditing(row.id);
  };

  const closeEditor = () => { setEditing(null); };

  const handleSave = async (e) => {
    e?.preventDefault?.();
    if (!shift?.id) return;
    setSaving(true);
    try {
      if (editing === 'new') {
        const payload = {
          service_catalog_id: form.service_catalog_id,
          participant_id: shift.participant_id,
          funding_plan_id: form.funding_plan_id,
          allocation_start_time: form.allocation_start_time,
          allocation_end_time: form.allocation_end_time,
          allocation_order: rows.length + 1,
          is_primary: form.is_primary,
          notes: form.notes || undefined,
        };
        await api.post(`/shifts/${shift.id}/service-allocations`, payload);
        toast.success('Service activity added');
      } else {
        const payload = {
          version: form._version,
          service_catalog_id: form.service_catalog_id,
          funding_plan_id: form.funding_plan_id,
          allocation_start_time: form.allocation_start_time,
          allocation_end_time: form.allocation_end_time,
          is_primary: form.is_primary,
          notes: form.notes || undefined,
        };
        await api.put(`/shifts/${shift.id}/service-allocations/${editing}`, payload);
        toast.success('Service activity updated');
      }
      closeEditor();
      await reload();
    } catch (err) {
      const d = err.response?.data?.detail || err.response?.data?.error;
      toast.error(d?.message || d?.code || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (row) => {
    if (!shift?.id) return;
    if (!window.confirm(`Archive activity "${row.allocation_start_time}–${row.allocation_end_time}"? This is operational only — invoices and payroll are unaffected.`)) return;
    try {
      await api.delete(`/shifts/${shift.id}/service-allocations/${row.id}`, { data: { reason: 'admin removed' } });
      toast.success('Activity archived');
      await reload();
    } catch (err) {
      const d = err.response?.data?.detail || err.response?.data?.error;
      toast.error(d?.message || d?.code || 'Failed to archive');
    }
  };

  // ── render ─────────────────────────────────────────────────────────────────
  if (!canView) return null;

  return (
    <Card data-testid="service-activity-breakdown-panel">
      <CardHeader className="pb-2 px-3 pt-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Layers className="w-4 h-4" /> Service Activity Breakdown
          {flagOn ? (
            <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">
              <CheckCircle2 className="w-3 h-3 mr-1" /> Enabled
            </Badge>
          ) : (
            <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">
              <Lock className="w-3 h-3 mr-1" /> Preview (flag OFF)
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-3 pt-0 space-y-2 text-sm">
        <div className="rounded-md border bg-blue-50 border-blue-200 px-2.5 py-2 text-[11px] leading-relaxed text-blue-900 flex gap-2">
          <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
          <span>
            Operational breakdown only. Staff still see one shift with a single clock-in/out.
            <strong className="block mt-0.5">Invoices, claims, payroll, and funding remain unchanged.</strong>
          </span>
        </div>

        {/* Enterprise Phase 1.5 — Operational Activity Tile (admin-only, umbrella-gated) */}
        {canViewOperationalTile && opsSummary?.feature_enabled && opsSummary?.summary && (
          <div
            className="rounded-md border border-slate-200 bg-white px-2.5 py-2 space-y-1.5"
            data-testid="operational-activity-tile"
          >
            <div className="flex items-center justify-between text-[11px] text-slate-700">
              <span className="flex items-center gap-1.5 font-medium">
                <Activity className="w-3.5 h-3.5" /> Operational Activity (admin)
              </span>
              <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">
                Read-only · Phase 1
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded border bg-slate-50 px-2 py-1" data-testid="ops-tile-activity-count">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Activities</div>
                <div className="tabular-nums font-semibold text-slate-800">{opsSummary.summary.activity_count ?? 0}</div>
              </div>
              <div className="rounded border bg-slate-50 px-2 py-1" data-testid="ops-tile-unique-services">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Services</div>
                <div className="tabular-nums font-semibold text-slate-800">{opsSummary.summary.unique_service_count ?? 0}</div>
              </div>
              <div className="rounded border bg-slate-50 px-2 py-1" data-testid="ops-tile-total-hours">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Total hours</div>
                <div className="tabular-nums font-semibold text-slate-800">{fmtHours(opsSummary.summary.total_activity_hours)}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5 pt-0.5">
              {opsSummary.summary.primary_activity_id && (
                <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700" data-testid="ops-tile-primary-tag">
                  <Star className="w-3 h-3 mr-1" /> Primary set
                </Badge>
              )}
              {opsSummary.summary.has_transport_marker && (
                <Badge variant="outline" className="text-[10px] border-indigo-300 text-indigo-700" data-testid="ops-tile-transport-tag">
                  <Car className="w-3 h-3 mr-1" /> Transport marker
                </Badge>
              )}
              {!opsSummary.summary.primary_activity_id && !opsSummary.summary.has_transport_marker && (
                <span className="text-[10px] text-slate-500" data-testid="ops-tile-no-tags">No operational tags</span>
              )}
            </div>
          </div>
        )}

        {/* Enterprise Phase 2A — Financial Preview Tile (admin-only, flag-gated, READ-ONLY) */}
        {canViewPreviewTile && preview?.feature_enabled && preview?.summary && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50/40 px-2.5 py-2 space-y-1.5"
            data-testid="preview-tile"
          >
            <div className="flex items-center justify-between text-[11px] text-amber-900">
              <span className="flex items-center gap-1.5 font-medium">
                <DollarSign className="w-3.5 h-3.5" /> Financial Preview (admin · projection)
              </span>
              <Badge
                variant="outline"
                className={
                  preview.summary.severity === 'CRITICAL'
                    ? 'text-[10px] border-red-400 text-red-700'
                    : preview.summary.severity === 'WARN'
                    ? 'text-[10px] border-amber-400 text-amber-700'
                    : 'text-[10px] border-emerald-300 text-emerald-700'
                }
                data-testid="preview-tile-severity"
              >
                {preview.summary.severity === 'CRITICAL' && <AlertTriangle className="w-3 h-3 mr-1" />}
                {preview.summary.severity}
              </Badge>
            </div>
            <div className="grid grid-cols-3 gap-2 text-[11px]">
              <div className="rounded border bg-white px-2 py-1" data-testid="preview-tile-lines">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Preview lines</div>
                <div className="tabular-nums font-semibold text-slate-800">{preview.summary.allocation_count}</div>
              </div>
              <div className="rounded border bg-white px-2 py-1" data-testid="preview-tile-hours">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Projected hours</div>
                <div className="tabular-nums font-semibold text-slate-800">{fmtHours(preview.summary.total_hours)}</div>
              </div>
              <div className="rounded border bg-white px-2 py-1" data-testid="preview-tile-amount">
                <div className="text-[9px] uppercase tracking-wide text-slate-500">Projected amount</div>
                <div className="tabular-nums font-semibold text-slate-800">
                  ${Number(preview.summary.total_amount || 0).toFixed(2)}
                </div>
              </div>
            </div>
            <div className="text-[10px] text-amber-900/80 italic">
              Read-only projection. Invoices, claims, payroll, and funding remain unchanged.
            </div>
            {preview.summary.severity !== 'INFO' && (
              <div
                className="rounded border border-amber-300 bg-white/60 px-2 py-1 text-[10px] text-amber-900"
                data-testid="preview-tile-drift-banner"
              >
                Reconciliation drift detected (
                {preview.reconciliation?.hours_delta != null
                  ? `${preview.reconciliation.hours_delta}h`
                  : '—'}
                ). This preview will NOT be promoted to live billing until the source
                allocations are corrected.
              </div>
            )}

            {/* Enterprise Phase 2A.5 — Preview Lines Drawer (admin · expandable · READ-ONLY) */}
            <button
              type="button"
              onClick={() => setPreviewLinesOpen((v) => !v)}
              className="w-full flex items-center justify-between text-[10px] text-slate-700 hover:text-slate-900 px-1 py-0.5 rounded hover:bg-amber-100/60 transition-colors"
              data-testid="preview-lines-drawer-toggle"
              aria-expanded={previewLinesOpen}
            >
              <span className="flex items-center gap-1 font-medium">
                {previewLinesOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                Preview lines ({(preview.lines || []).length})
              </span>
              <span className="italic text-slate-500">Projection · no live writes</span>
            </button>
            {previewLinesOpen && (
              <div className="space-y-1.5 pt-1" data-testid="preview-lines-drawer">
                {/* Phase 2A.6 — Legacy Invoice Comparison Bar (admin · read-only) */}
                {preview.reconciliation && (
                  (() => {
                    const legacy = Number(preview.reconciliation.legacy_invoice_amount || 0);
                    const projected = Number(preview.reconciliation.projected_amount || 0);
                    const delta = Number(preview.reconciliation.amount_delta || 0);
                    const sev = preview.reconciliation.amount_severity || preview.summary.severity;
                    const status = sev === 'INFO' ? 'MATCH'
                      : sev === 'WARN' ? 'MINOR_VARIANCE'
                      : 'MATERIAL_DIFFERENCE';
                    const statusClass = status === 'MATCH'
                      ? 'border-emerald-300 text-emerald-700 bg-emerald-50'
                      : status === 'MINOR_VARIANCE'
                      ? 'border-amber-300 text-amber-700 bg-amber-50'
                      : 'border-red-300 text-red-700 bg-red-50';
                    const pct = legacy > 0 ? ((delta / legacy) * 100).toFixed(2) : null;
                    return (
                      <div
                        className="rounded border border-slate-200 bg-white px-2.5 py-2 grid grid-cols-4 gap-2 items-center"
                        data-testid="comparison-bar"
                      >
                        <div data-testid="comparison-bar-legacy">
                          <div className="text-[9px] uppercase tracking-wide text-slate-500">Legacy invoice</div>
                          <div className="tabular-nums text-[11px] font-semibold text-slate-800">
                            ${legacy.toFixed(2)}
                          </div>
                        </div>
                        <div data-testid="comparison-bar-projected">
                          <div className="text-[9px] uppercase tracking-wide text-slate-500">Projected preview</div>
                          <div className="tabular-nums text-[11px] font-semibold text-slate-800">
                            ${projected.toFixed(2)}
                          </div>
                        </div>
                        <div data-testid="comparison-bar-delta">
                          <div className="text-[9px] uppercase tracking-wide text-slate-500">Difference</div>
                          <div className="tabular-nums text-[11px] font-semibold text-slate-800">
                            ${delta.toFixed(2)}
                            {pct != null && (
                              <span className="text-[9px] text-slate-500 ml-1">({pct}%)</span>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge
                            variant="outline"
                            className={`text-[9px] ${statusClass}`}
                            data-testid="comparison-bar-status"
                          >
                            {status}
                          </Badge>
                        </div>
                      </div>
                    );
                  })()
                )}

                {(preview.lines || []).length === 0 ? (
                  <div className="text-[10px] text-slate-500 italic px-1 py-2" data-testid="preview-lines-empty">
                    No preview lines available.
                  </div>
                ) : (
                  (preview.lines || []).map((ln) => (
                    <div
                      key={ln.allocation_id}
                      className="rounded border border-amber-200 bg-white px-2 py-1.5 text-[10px] space-y-0.5"
                      data-testid={`preview-line-row-${ln.allocation_id}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-1.5 font-medium tabular-nums text-slate-800">
                          #{ln.allocation_order ?? '—'} ·{' '}
                          {ln.allocation_start_time || '—'}–{ln.allocation_end_time || '—'}
                          {ln.is_primary && (
                            <Badge variant="outline" className="text-[9px] border-emerald-300 text-emerald-700">
                              <Star className="w-2.5 h-2.5 mr-0.5" /> Primary
                            </Badge>
                          )}
                          {ln.transport_segment && (
                            <Badge variant="outline" className="text-[9px] border-indigo-300 text-indigo-700">
                              <Car className="w-2.5 h-2.5 mr-0.5" /> Transport
                            </Badge>
                          )}
                        </div>
                        <span className="tabular-nums font-semibold text-slate-800" data-testid={`preview-line-amount-${ln.allocation_id}`}>
                          ${Number(ln.amount || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-[9px] text-slate-600">
                        <div>
                          <span className="text-slate-400 uppercase tracking-wide">Support item</span>
                          <div className="text-slate-700 truncate" title={ln.support_item_number || '—'}>
                            {ln.support_item_number || '—'}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-400 uppercase tracking-wide">Hours × Rate</span>
                          <div className="tabular-nums text-slate-700">
                            {fmtHours(ln.duration_hours)} × ${Number(ln.rate || 0).toFixed(2)}
                          </div>
                        </div>
                        <div>
                          <span className="text-slate-400 uppercase tracking-wide">Funding plan</span>
                          <div className="text-slate-700 truncate" title={fundingPlanLabel(fundingPlanLookup.get(ln.funding_plan_id)) || ln.funding_plan_id || '—'}>
                            {ln.funding_plan_id
                              ? fundingPlanLabel(fundingPlanLookup.get(ln.funding_plan_id) || { id: ln.funding_plan_id })
                              : '—'}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {(preview.issues || []).length > 0 && (
                  <div
                    className="rounded border border-amber-300 bg-amber-50 px-2 py-1 text-[10px] text-amber-900 space-y-0.5"
                    data-testid="preview-lines-issues"
                  >
                    <div className="font-medium flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Preview issues
                    </div>
                    {preview.issues.map((iss, idx) => (
                      <div key={idx} className="pl-4 text-[9px]" data-testid={`preview-issue-${idx}`}>
                        <span className="font-mono">{iss.code}</span>
                        <span className="text-slate-500"> · {iss.severity}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!flagOn && (
          <div className="rounded-md border border-amber-300 bg-amber-50 px-2.5 py-2 text-[11px] text-amber-900">
            Feature flag <span className="font-mono">multi_service_shift_allocations_enabled</span> is OFF.
            Mutations are blocked in production. Use the admin Feature Flags page to enable for internal QA only.
          </div>
        )}

        {loading ? (
          <div className="text-xs text-muted-foreground">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-xs text-muted-foreground py-2">
            No service activities recorded yet. {flagOn && canCreate ? 'Click "Add activity" to break this shift into multiple services.' : ''}
          </div>
        ) : (
          <div className="space-y-1.5">
            {rows.sort((a, b) => (a.allocation_order || 0) - (b.allocation_order || 0)).map(row => (
              <div
                key={row.id}
                className="rounded-md border bg-slate-50 p-2 flex items-start justify-between gap-2 text-xs"
                data-testid={`activity-row-${row.id}`}
              >
                <div className="flex-1 space-y-0.5">
                  <div className="flex items-center gap-1.5 font-medium tabular-nums">
                    {row.allocation_start_time}–{row.allocation_end_time}
                    <span className="text-muted-foreground font-normal">· {fmtHours(row.duration_hours)}</span>
                    {row.is_primary && (
                      <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">Primary (billed)</Badge>
                    )}
                  </div>
                  <div className="text-muted-foreground">
                    Service: <span className="text-foreground">{serviceLabel(row.service_catalog_id)}</span>
                  </div>
                  {row.notes && <div className="text-muted-foreground italic">"{row.notes}"</div>}
                </div>
                <div className="flex gap-1 shrink-0">
                  {flagOn && canUpdate && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={() => openEdit(row)} data-testid={`activity-edit-${row.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                  )}
                  {flagOn && canDelete && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleArchive(row)} data-testid={`activity-delete-${row.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Allocation summary */}
        {rows.length > 0 && (
          <div className="flex items-center justify-between text-[11px] text-muted-foreground border-t pt-1.5">
            <span>Shift duration: <span className="tabular-nums font-medium">{fmtHours(shiftDuration)}</span></span>
            <span>Allocated: <span className={`tabular-nums font-medium ${overAllocated ? 'text-red-600' : ''}`}>{fmtHours(allocated)}</span></span>
            <span>Remaining: <span className="tabular-nums font-medium">{fmtHours(remaining)}</span></span>
          </div>
        )}

        {/* Action buttons */}
        {flagOn && canCreate && !editing && (
          <div className="pt-1">
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs w-full"
              onClick={openNew}
              data-testid="add-service-activity-btn"
            >
              <Plus className="w-3 h-3 mr-1" /> Add activity
            </Button>
          </div>
        )}

        {/* Inline editor */}
        {editing && (
          <form onSubmit={handleSave} className="rounded-md border bg-background p-2.5 space-y-2 text-xs" data-testid="activity-editor">
            <div className="flex items-center justify-between">
              <div className="font-medium">{editing === 'new' ? 'New activity' : 'Edit activity'}</div>
              <Button type="button" size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={closeEditor}>
                <X className="w-3 h-3" />
              </Button>
            </div>
            <Separator />
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-[10px]">Start</Label>
                <Input
                  type="time"
                  value={form.allocation_start_time}
                  onChange={(e) => setForm(f => ({ ...f, allocation_start_time: e.target.value }))}
                  required
                  className="h-7 text-xs"
                  data-testid="activity-start-input"
                />
              </div>
              <div>
                <Label className="text-[10px]">End</Label>
                <Input
                  type="time"
                  value={form.allocation_end_time}
                  onChange={(e) => setForm(f => ({ ...f, allocation_end_time: e.target.value }))}
                  required
                  className="h-7 text-xs"
                  data-testid="activity-end-input"
                />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between -mb-1">
                <Label className="text-[10px]">Service</Label>
                {(() => {
                  // Funded services for this participant (+ optional funding plan cross-filter)
                  const fundedIds = new Set(
                    (participantServiceItems || [])
                      .filter(psi => !form.funding_plan_id || psi.funding_plan_id === form.funding_plan_id)
                      .map(psi => psi.service_catalog_id)
                  );
                  const fundedCount = fundedIds.size;
                  if (fundedCount > 0) {
                    return (
                      <button type="button"
                        onClick={() => setShowAllServices(s => !s)}
                        className="text-[10px] text-primary hover:underline"
                        data-testid="activity-service-toggle-all">
                        {showAllServices ? `Funded only (${fundedCount})` : `Show all (${services.length})`}
                      </button>
                    );
                  }
                  return null;
                })()}
              </div>
              <Select value={form.service_catalog_id} onValueChange={(v) => setForm(f => ({ ...f, service_catalog_id: v }))}>
                <SelectTrigger className="h-7 text-xs" data-testid="activity-service-select"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>
                  {(() => {
                    const fundedIds = new Set(
                      (participantServiceItems || [])
                        .filter(psi => !form.funding_plan_id || psi.funding_plan_id === form.funding_plan_id)
                        .map(psi => psi.service_catalog_id)
                    );
                    const list = (showAllServices || fundedIds.size === 0)
                      ? services
                      : services.filter(s => fundedIds.has(s.id));
                    const emptyHint = fundedIds.size === 0 && (participantServiceItems || []).length === 0;
                    return (
                      <>
                        {emptyHint && (
                          <div className="px-2 py-1.5 text-[10px] text-amber-700 bg-amber-50">
                            No services funded for this participant. Showing full catalog.
                          </div>
                        )}
                        {list.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} ({s.code})</SelectItem>
                        ))}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px]">Funding plan</Label>
              <Select value={form.funding_plan_id} onValueChange={(v) => setForm(f => ({ ...f, funding_plan_id: v }))}>
                <SelectTrigger className="h-7 text-xs" data-testid="activity-funding-select"><SelectValue placeholder="Select funding plan" /></SelectTrigger>
                <SelectContent>
                  {fundingPlans.length === 0 && (
                    <div className="px-2 py-2 text-xs text-muted-foreground">
                      No funding plans for this participant. Add one from the Participant → Funding tab.
                    </div>
                  )}
                  {fundingPlans.map(p => (
                    <SelectItem key={p.id} value={p.id}>{fundingPlanLabel(p)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="alloc-primary"
                checked={form.is_primary}
                onChange={(e) => setForm(f => ({ ...f, is_primary: e.target.checked }))}
                className="h-3.5 w-3.5 rounded accent-primary"
                data-testid="activity-primary-checkbox"
              />
              <Label htmlFor="alloc-primary" className="text-[10px] cursor-pointer">
                Mark as primary (operational tag — billing remains on the existing shift service)
              </Label>
            </div>
            <div>
              <Label className="text-[10px]">Notes (optional)</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                className="h-7 text-xs"
                placeholder="e.g. handover 10:30"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={closeEditor}>Cancel</Button>
              <Button type="submit" size="sm" className="h-7 text-xs" disabled={saving} data-testid="activity-save-btn">
                {saving ? 'Saving…' : (editing === 'new' ? 'Add activity' : 'Save changes')}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
    </Card>
  );
}

export default ServiceActivityBreakdownPanel;
